import React, { useState } from 'react';
import Breadcrumb from '../../components/common/Breadcrumb';
import { IconDownload, IconX, IconReceipt } from '@tabler/icons-react';
import { useMyPayslips, useMyPayslip, useDownloadPayslip } from '../../hooks/usePayroll';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { PageLoader, TableLoader } from '../../components/common/Loader';

const fmt = (v) =>
  Number(v || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function PayslipDetailModal({ payslipId, orgId, onClose, canDownload }) {
  const { data: payslip, isLoading } = useMyPayslip(payslipId, orgId);
  const downloadPayslip = useDownloadPayslip();

  if (!payslipId) return null;

  const earnings = payslip?.lines?.filter((l) => l.line_type === 'earning') || [];
  const benefits = payslip?.lines?.filter((l) => l.line_type === 'benefit') || [];
  const deductions = payslip?.lines?.filter((l) => l.line_type === 'deduction') || [];

  const handleDownload = () => {
    downloadPayslip.mutate(
      {
        id: payslipId,
        employeeName: payslip?.employee_name,
        runLabel: payslip?.run_label,
        orgId,
      },
      {
        onSuccess: () => toast.success('Payslip downloaded.'),
        onError: () => toast.error('Failed to download payslip.'),
      }
    );
  };

  return (
    <>
      <div className="fixed inset-0 z-[1001] flex items-center justify-center p-15">
        <div className="bg-bg-dark rounded-lg shadow-light border border-border w-full max-w-[650px] max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-20 border-b border-border sticky top-0 bg-bg-dark z-10">
            <div>
              <h3 className="text-[16px]/[20px] font-semibold">
                {payslip?.run_label || 'Payslip Detail'}
              </h3>
              {payslip && (
                <p className="text-text-secondary text-sm mt-3">{payslip.employee_name}</p>
              )}
            </div>
            <div className="flex items-center gap-10">
              {canDownload && (
                <button
                  onClick={handleDownload}
                  disabled={!payslip || downloadPayslip.isPending}
                  className="btn btn-outline-secondary flex items-center gap-5"
                >
                  <IconDownload size={16} />
                  {downloadPayslip.isPending ? 'Downloading...' : 'Download PDF'}
                </button>
              )}
              <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
                <IconX size={20} />
              </button>
            </div>
          </div>

          <div className="p-20">
            {isLoading ? (
              <TableLoader text="Loading payslip..." />
            ) : !payslip ? (
              <TableLoader text="Payslip not found." />
            ) : (
              <div className="flex flex-col gap-20">
                {/* Earnings */}
                {earnings.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-success mb-10">Earnings</p>
                    <table className="w-full text-sm">
                      <tbody>
                        {earnings.map((line) => (
                          <tr key={line.id} className="border-t border-border hover:bg-bg-light">
                            <td className="py-8 px-5">{line.label}</td>
                            <td className="py-8 px-5 text-right">₦{fmt(line.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Benefits */}
                {benefits.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-sky mb-10">Benefits</p>
                    <table className="w-full text-sm">
                      <tbody>
                        {benefits.map((line) => (
                          <tr key={line.id} className="border-t border-border hover:bg-bg-light">
                            <td className="py-8 px-5">{line.label}</td>
                            <td className="py-8 px-5 text-right">₦{fmt(line.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Deductions */}
                {deductions.length > 0 && (
                  <div>
                    <p className="text-sm font-semibold text-danger mb-10">Deductions</p>
                    <table className="w-full text-sm">
                      <tbody>
                        {deductions.map((line) => (
                          <tr key={line.id} className="border-t border-border hover:bg-bg-light">
                            <td className="py-8 px-5">{line.label}</td>
                            <td className="py-8 px-5 text-right">₦{fmt(line.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Totals */}
                <div className="bg-bg-light rounded-lg p-15 flex flex-col gap-8">
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">Total Earnings</span>
                    <span>₦{fmt(payslip.total_earnings)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">Total Deductions</span>
                    <span className="text-danger">— ₦{fmt(payslip.total_deductions)}</span>
                  </div>
                  <div className="flex justify-between text-[16px]/[20px] font-semibold border-t border-border pt-8 mt-5">
                    <span>Net Pay</span>
                    <span>₦{fmt(payslip.net_pay)}</span>
                  </div>
                </div>

                {/* Signatories */}
                {(payslip.signatory_1_name || payslip.signatory_2_name) && (
                  <div className="border-t border-border pt-15 flex gap-30 flex-wrap">
                    {payslip.signatory_1_name && (
                      <div>
                        <p className="text-xs text-text-secondary mb-3">Signatory 1</p>
                        <p className="text-sm font-medium">{payslip.signatory_1_name}</p>
                        <p className="text-xs text-text-secondary">{payslip.signatory_1_title}</p>
                      </div>
                    )}
                    {payslip.signatory_2_name && (
                      <div>
                        <p className="text-xs text-text-secondary mb-3">Signatory 2</p>
                        <p className="text-sm font-medium">{payslip.signatory_2_name}</p>
                        <p className="text-xs text-text-secondary">{payslip.signatory_2_title}</p>
                      </div>
                    )}
                  </div>
                )}

                {payslip.generated_at && (
                  <p className="text-xs text-text-secondary">
                    Generated: {new Date(payslip.generated_at).toLocaleDateString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div onClick={onClose} className="fixed z-[1000] inset-0 bg-black/50 backdrop-blur-[3px]" />
    </>
  );
}

export default function Payslip() {
  const orgId = localStorage.getItem('orgId') || '';
  const { hasAnyPermission } = useAuth();
  const canDownload = !hasAnyPermission(['rbacapp.pay_approve']);
  const [selectedId, setSelectedId] = useState(null);
  const downloadPayslip = useDownloadPayslip();

  const { data: payslips, isLoading } = useMyPayslips(orgId);

  const handleDirectDownload = (payslip, e) => {
    e.stopPropagation();
    downloadPayslip.mutate(
      { id: payslip.id, runLabel: payslip.run_label, orgId },
      {
        onSuccess: () => toast.success('Payslip downloaded.'),
        onError: () => toast.error('Failed to download payslip.'),
      }
    );
  };

  return (
    <>
      <Breadcrumb title="My Payslips" path="Payroll / My Payslips" />

      <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
        <h2 className="text-[16px]/[20px] font-semibold mb-5">My Payslips</h2>
        <p className="text-text-secondary text-sm mb-20">
          Payslips are released after each payroll run is published. Click a payslip to view details.
        </p>

        {isLoading ? (
          <TableLoader text="Loading payslips..." />
        ) : !payslips?.length ? (
          <div className="flex flex-col items-center justify-center py-40 gap-10 text-text-secondary">
            <IconReceipt size={40} className="opacity-30" />
            <p>No payslips available yet.</p>
            <p className="text-sm">Payslips appear here once your payroll is published.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-10">
            {payslips.map((payslip) => (
              <div
                key={payslip.id}
                onClick={() => setSelectedId(payslip.id)}
                className="flex items-center justify-between gap-15 p-15 bg-bg-light rounded-lg border border-border hover:border-primary cursor-pointer transition-colors"
              >
                <div className="flex flex-col gap-3">
                  <p className="font-medium">{payslip.run_label}</p>
                  {payslip.generated_at && (
                    <p className="text-xs text-text-secondary">
                      {new Date(payslip.generated_at).toLocaleDateString('en-GB', {
                        day: '2-digit', month: 'short', year: 'numeric',
                      })}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-20">
                  <div className="text-right hidden sm:block">
                    <p className="text-xs text-text-secondary">Gross Pay</p>
                    <p className="text-sm">₦{fmt(payslip.gross_pay)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-text-secondary">Net Pay</p>
                    <p className="font-semibold">₦{fmt(payslip.net_pay)}</p>
                  </div>
                  {canDownload && (
                    <button
                      onClick={(e) => handleDirectDownload(payslip, e)}
                      disabled={downloadPayslip.isPending}
                      className="btn btn-outline-secondary flex items-center gap-5 shrink-0"
                      title="Download PDF"
                    >
                      <IconDownload size={16} />
                      <span className="hidden sm:inline">PDF</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <PayslipDetailModal
        payslipId={selectedId}
        orgId={orgId}
        onClose={() => setSelectedId(null)}
        canDownload={canDownload}
      />
    </>
  );
}
